import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';

import {
  Pane,
  Paragraph,
  TextInputField,
  Checkbox,
  Badge,
  IconButton,
  Select,
  SelectField,
  TrashIcon,
  MapMarkerIcon,
} from 'evergreen-ui';

import { ALL_COUNTRIES } from "../../utils/countries";

function AddressItem(props) {
  const publisherGlobalData = useSelector((state) => state.publisherGlobalData.value);
  const enableSalesByRegion = publisherGlobalData.enable_sales_by_region;

  const [cleandata, setCleandata] = useState({});
  const isPrimary =
    props.address.primary && props.address.primary === true ? true : false;

  const handleUpdateStaged = (obj) => {
    setCleandata((oldState) => ({ ...oldState, [obj.key]: obj.val }));
  };

  const handleCleanData = useCallback(() => {
    let fields = [
      'id',
      'tmpid',
      'primary',
      'line1',
      'line2',
      'city',
      'stateOrRegion',
      'zipcode',
      'country',
      'addressType',
    ];
    let cleandata = {};
    fields.forEach((field) => {
      if (!props.address[field]) {
        cleandata[field] = '';
      } else {
        cleandata[field] = props.address[field];
      }
    });
    return cleandata;
  }, [props.address]);

  const handleUpdateAddress = (obj) => {
    // If we're updating the country field and it appears to be a complex object or JSON string
    if (obj.key === 'country') {
      // Normalize country to be just the name string
      if (typeof obj.val === 'object' && obj.val.name) {
        obj.val = obj.val.name;
      } else if (typeof obj.val === 'string' && obj.val.includes('{')) {
        try {
          // Try to extract the name using regex
          const match = obj.val.match(/'name':\s*'([^']+)'/);
          if (match && match[1]) {
            obj.val = match[1];
          }
        } catch (e) {}
      }
    }
    
    handleUpdateStaged(obj);
    let updated = { ...cleandata, [obj.key]: obj.val };
    props.handleUpdateAddressItem(updated, 'Address');
  };

  const countryOptions = useMemo(() => {
    return ALL_COUNTRIES.map(c => ({ label: `${c.name} (${c.code})`, value: c.code, name: c.name }));
  }, []);

  const handleCountryChange = (countryCode) => {
    const selectedCountry = ALL_COUNTRIES.find(c => c.code === countryCode);
    if (selectedCountry) {
      // Always store just the country name for consistency
      handleUpdateAddress({
        key: "country",
        val: selectedCountry.name
      });
    } else {
      handleUpdateAddress({ key: "country", val: "" });
    }
  };

  let addressType = '';
  if (
    cleandata.hasOwnProperty('addressType') &&
    props.itemType === 'customer'
  ) {
    if (props.editing) {
      addressType = (
        <AddressType
          objKey="addressType"
          handleUpdateAddressItem={handleUpdateAddress}
          myKey="addressType"
          value={cleandata.addressType}
          editing={props.editing}
        />
      );
    } else {
      addressType = (
        <Paragraph>
          <Badge color="neutral">
            {cleandata.addressType ? cleandata.addressType : 'OTHER'}
          </Badge>
        </Paragraph>
      );
    }
  }

  useEffect(() => {
    let data = handleCleanData();
    setCleandata(data);
  }, [props.address, handleCleanData]);

  useEffect(() => {
    if (props.cancelled === true) {
      let data = handleCleanData();
      setCleandata(data);
    }
  }, [props.cancelled, handleCleanData]);

  // Helper to get display text for country
  const getCountryDisplayText = () => {
    // Guard against undefined or null
    if (!cleandata.country) return '';
    
    // If country is an object with name property, use that
    if (typeof cleandata.country === 'object' && cleandata.country.name) {
      return cleandata.country.name;
    }
    
    // Handle the case where it's a string representation of an object with single quotes
    // Example: "{'code': 'DE', 'name': 'Germany'}"
    if (typeof cleandata.country === 'string' && cleandata.country.includes("'code':")) {
      try {
        // Extract just the name using regex
        const nameMatch = cleandata.country.match(/'name':\s*'([^']+)'/);
        if (nameMatch && nameMatch[1]) {
          return nameMatch[1];
        }
      } catch (e) {
        console.error("Error parsing country string with regex:", e);
      }
    }
    
    // Handle stringified JSON objects with curly braces
    if (typeof cleandata.country === 'string' && cleandata.country.startsWith('{')) {
      try {
        // First try to replace single quotes with double quotes for valid JSON
        const fixedJson = cleandata.country.replace(/'/g, '"');
        const parsedCountry = JSON.parse(fixedJson);
        if (parsedCountry && parsedCountry.name) {
          return parsedCountry.name;
        }
      } catch (e) {
        console.error("Error parsing country JSON:", e);
      }
    }
    
    // For simple string values (like "United States of America" or "Armenia")
    return cleandata.country;
  };

  return (
    <Pane padding={8}>
      <Pane display="flex" flexDirection="column">
        {props.editing ? (
          <Pane>
            <Pane display="flex">
              <Checkbox
                label="Primary Address?"
                checked={isPrimary}
                onChange={(e) => {
                  handleUpdateAddress({ key: 'primary', val: !isPrimary });
                }}
              />
              {cleandata.hasOwnProperty('addressType') && addressType}

              <IconButton
                icon={TrashIcon}
                intent="danger"
                onClick={(e) => props.handleDelete(cleandata)}
                marginLeft={8}
              />
            </Pane>
            <Pane display="flex" marginBottom={8}>
              <Pane>
                <Paragraph size={300} color="#667b7f">
                  Line 1
                </Paragraph>
                <AddressLine
                  label="Line 1"
                  objKey="line1"
                  value={cleandata.line1}
                  editing={props.editing}
                  handleUpdateAddressItem={handleUpdateAddress}
                />
              </Pane>
              <Pane marginLeft={8}>
                <Paragraph size={300} color="#667b7f">
                  Line 2
                </Paragraph>
                <AddressLine
                  label="Line 2"
                  objKey="line2"
                  value={cleandata.line2}
                  editing={props.editing}
                  handleUpdateAddressItem={handleUpdateAddress}
                />
              </Pane>
            </Pane>
            <Pane display="flex" marginBottom={8}>
              <Pane>
                <Paragraph size={300} color="#667b7f">
                  City
                </Paragraph>
                <AddressLine
                  objKey="city"
                  value={cleandata.city}
                  editing={props.editing}
                  handleUpdateAddressItem={handleUpdateAddress}
                />
              </Pane>
              <Pane marginLeft={8}>
                <Paragraph size={300} color="#667b7f">
                  State/Region
                </Paragraph>
                <AddressLine
                  objKey="stateOrRegion"
                  value={cleandata.stateOrRegion}
                  editing={props.editing}
                  handleUpdateAddressItem={handleUpdateAddress}
                />
              </Pane>
            </Pane>
            <Pane display="flex">
              <Pane>
                <Paragraph size={300} color="#667b7f">
                  Postal Code
                </Paragraph>
                <AddressLine
                  objKey="zipcode"
                  value={cleandata.zipcode}
                  editing={props.editing}
                  handleUpdateAddressItem={handleUpdateAddress}
                />
              </Pane>
              <Pane marginLeft={8}>
                <Paragraph size={300} color="#667b7f">
                  Country
                </Paragraph>
                {enableSalesByRegion ? (
                  <SelectField
                    label=""
                    value={(() => {
                      const countryName = getCountryDisplayText();
                      // Find the country code based on the display name
                      const country = ALL_COUNTRIES.find(c => c.name === countryName);
                      return country ? country.code : "";
                    })()}
                    onChange={e => handleCountryChange(e.target.value)}
                    marginY={0}
                    width="100%"
                  >
                    <option value="" disabled>Select a country...</option>
                    {countryOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </SelectField>
                ) : (
                  <AddressLine
                    objKey="country"
                    value={cleandata.country}
                    editing={props.editing}
                    handleUpdateAddressItem={handleUpdateAddress}
                  />
                )}
              </Pane>
            </Pane>
          </Pane>
        ) : (
          <Pane>
            <Pane
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              marginBottom={8}
            >
              <Pane display="flex" alignItems="center">
                <MapMarkerIcon
                  color={isPrimary ? 'success' : 'muted'}
                  marginRight={8}
                />
                {cleandata.hasOwnProperty('addressType') && addressType}
              </Pane>

              {isPrimary && (
                <Paragraph size={300} color="var(--robinegg)">
                  (Primary)
                </Paragraph>
              )}
            </Pane>
            <Paragraph size={400}>
              {['line1', 'line2']
                .map((item) => cleandata[item])
                .filter(Boolean)
                .join(', ')}
            </Paragraph>
            <Paragraph size={400}>
              {['city', 'stateOrRegion']
                .map((item) => cleandata[item])
                .filter(Boolean)
                .join(', ')}{' '}
              {cleandata.zipcode}
            </Paragraph>
            <Paragraph size={400}>{getCountryDisplayText()}</Paragraph>
          </Pane>
        )}
      </Pane>
    </Pane>
  );
}

const AddressLine = (props) => {
  return (
    <Pane>
      <TextInputField
        label=""
        className="item-form"
        onChange={(e) =>
          props.handleUpdateAddressItem({
            key: props.objKey,
            val: e.target.value,
          })
        }
        value={props.value}
        disabled={props.editing ? false : true}
        marginY={0}
      />
    </Pane>
  );
};

const AddressType = (props) => {
  return (
    <Pane marginRight={32}>
      <Paragraph size={300} color="#667b7f">
        Address Type
      </Paragraph>

      <Select
        value={props.value}
        onChange={(e) =>
          props.handleUpdateAddressItem({
            key: props.objKey,
            val: e.target.value,
          })
        }
      >
        <option value="">None</option>
        <option value="Shipping">Shipping</option>
        <option value="Billing">Billing </option>
        <option value="Other">Other</option>
      </Select>
    </Pane>
  );
};

export default AddressItem;
